import { events, ServerSentEventMessage } from 'fetch-event-stream';
import * as React from 'react';

interface MutationOptions<TVariables = unknown> {
	mutationFn: (variables: TVariables) => Promise<Response>;
	onError?: (error: Error, variables: TVariables) => void;
	onEvent: (event: ServerSentEventMessage, variables: TVariables) => void;
	onSuccess?: (params: TVariables) => void;
}

export type StreamingStatus = 'idle' | 'loading' | 'streaming' | 'error';

export function useStreamingResponseMutation<TVariables = unknown>({
	mutationFn,
	onError,
	onEvent,
	onSuccess,
}: MutationOptions<TVariables>) {
	const [status, setStatusState] = React.useState<StreamingStatus>('idle');
	// Mirrors `status` synchronously so a send in the same tick as `mutate` sees it before the re-render.
	const statusRef = React.useRef<StreamingStatus>('idle');
	const setStatus = React.useCallback((next: StreamingStatus) => {
		statusRef.current = next;
		setStatusState(next);
	}, []);
	const [error, setError] = React.useState<Error | null>(null);
	const abortControllerRef = React.useRef<AbortController | null>(null);
	const callbacksRef = React.useRef({
		mutationFn,
		onError,
		onEvent,
		onSuccess,
	});
	React.useEffect(() => {
		callbacksRef.current = {
			mutationFn,
			onError,
			onEvent,
			onSuccess,
		};
	}, [mutationFn, onError, onEvent, onSuccess]);
	const mutate = React.useCallback(
		async (params: TVariables) => {
			setStatus('loading');
			setError(null);
			const controller = new AbortController();
			abortControllerRef.current = controller;
			// Only the newest run owns `status`; an overlapped or reset run must not flip it.
			const setOwnStatus = (next: StreamingStatus) => {
				if (abortControllerRef.current === controller) {
					setStatus(next);
				}
			};
			try {
				const response = await callbacksRef.current.mutationFn(params);
				if (controller.signal.aborted) return;
				const stream = events(response, controller.signal);
				for await (const event of stream) {
					// A reset run only stops at its next chunk; it must not touch the run that replaced it.
					if (controller.signal.aborted || event.event === 'Heartbeat') {
						continue;
					}
					setOwnStatus('streaming');
					callbacksRef.current.onEvent(event, params);
				}
				if (controller.signal.aborted) return;
				setOwnStatus('idle');
				callbacksRef.current.onSuccess?.(params);
			} catch (e) {
				if (controller.signal.aborted) return;
				const err = e as Error;
				setError(err);
				setOwnStatus('error');
				callbacksRef.current.onError?.(err, params);
			}
		},
		[setStatus]
	);
	const getIsBusy = React.useCallback(() => statusRef.current === 'loading' || statusRef.current === 'streaming', []);
	const isLoading = status === 'loading';
	const isStreaming = status === 'streaming';
	const isError = status === 'error';
	const reset = () => {
		abortControllerRef.current?.abort();
		abortControllerRef.current = null;
		setStatus('idle');
		setError(null);
	};
	return { mutate, isLoading, isStreaming, isError, error, reset, status, getIsBusy };
}
